import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

// Also read by the pre-paint script in index.html — change both together.
export const NEXTGEN_THEME_KEY = "nextgen-theme";
export const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

const isThemeMode = (value: string | null): value is ThemeMode =>
  value === "light" || value === "dark" || value === "system";

// Light until the user says otherwise — syncing with the OS is opt-in.
export const DEFAULT_THEME_MODE: ThemeMode = "light";

const readStoredMode = (): ThemeMode => {
  const stored = localStorage.getItem(NEXTGEN_THEME_KEY);
  return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE;
};

export interface NextGenTheme {
  themeMode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setThemeMode: (mode: ThemeMode) => void;
}

/**
 * Owns the nextgen theme: the chosen mode, what it resolves to, and the `.dark`
 * class consumers render from. Drops the class on unmount — it's global, and
 * legacy pages have no dark theme. Call once, from the nextgen layout.
 */
export const useNextGenTheme = (): NextGenTheme => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(readStoredMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia(DARK_MEDIA_QUERY).matches
  );

  useEffect(() => {
    const query = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const resolvedTheme: ResolvedTheme =
    themeMode === "system" ? (systemPrefersDark ? "dark" : "light") : themeMode;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", resolvedTheme === "dark");
  }, [resolvedTheme]);

  useEffect(() => () => document.documentElement.classList.remove("dark"), []);

  const changeThemeMode = useCallback((mode: ThemeMode) => {
    localStorage.setItem(NEXTGEN_THEME_KEY, mode);
    setThemeMode(mode);
  }, []);

  return { themeMode, resolvedTheme, setThemeMode: changeThemeMode };
};
